import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2, UserPlus, Building2, ArrowLeft, Check, Search } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingCui, setLoadingCui] = useState(false);
  const [cuiVerified, setCuiVerified] = useState(false);
  const [caenValid, setCaenValid] = useState(null); // null = not checked, true = valid, false = invalid
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    // Date firmă
    companyName: '',
    cui: '',
    jNumber: '',
    address: '',
    codCAEN: '',
    
    // Date bancare
    bankAccount: '',
    bankName: '',
    
    // Date reprezentant
    representativeName: '',
    representativeCnp: '',
    representativeIdSeries: '',
    representativeIdNumber: '',
    representativeIdIssuer: '',
    representativeIdIssueDate: '',
    representativeIdExpiryDate: '',
    representativeAddress: '',
    
    // Date contact
    email: '',
    phone: '',
    website: '',
    
    // Parolă
    password: '',
    confirmPassword: '',
    
    // Setări
    headerColor: '#2563eb',
    isAnpcIntermediary: false,
    desiredPlan: 'basic'
  });

  const steps = [
    { title: 'Date Firmă', icon: Building2 },
    { title: 'Date Reprezentant', icon: UserPlus },
    { title: 'Contact & Setări', icon: Check }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (error) setError('');
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
    if (error) setError('');
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  const verifyCui = async () => {
    const cui = formData.cui.trim();
    
    if (!cui || cui.length < 2) {
      setError('Introduceți un CUI valid (minim 2 cifre)');
      return;
    }
    
    setLoadingCui(true);
    setError('');
    setCuiVerified(false);
    setCaenValid(null);
    
    try {
      const response = await axios.get(`${API_URL}/api/auth/verify-cui/${cui}`);
      
      if (response.data.success) {
        const data = response.data.data;
        
        // Auto-completează câmpurile
        setFormData(prev => ({
          ...prev,
          companyName: data.denumire || prev.companyName,
          jNumber: data.nrRegCom || prev.jNumber,
          address: data.adresa || prev.address,
          codCAEN: data.codCAEN || prev.codCAEN
        }));
        
        setCuiVerified(true);
        
        // Verifică dacă codul CAEN este valid pentru intermediari de credite
        const validCAENCodes = ['6619', '6492', '6499'];
        const isCAENValid = validCAENCodes.includes(data.codCAEN);
        setCaenValid(isCAENValid);
        
        if (isCAENValid) {
          toast({
            title: "✅ Date preluate de la ANAF",
            description: `Firma: ${data.denumire} | CAEN: ${data.codCAEN} ✓`,
            variant: "default"
          });
        } else {
          toast({
            title: "⚠️ Cod CAEN neacceptat",
            description: `Codul CAEN ${data.codCAEN} nu este eligibil pentru platforma FINBRO. Contactați administratorul.`,
            variant: "destructive"
          });
        }
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Eroare la verificarea CUI-ului';
      setError(message);
      setCuiVerified(false);
      setCaenValid(null);
    } finally {
      setLoadingCui(false);
    }
  };

  const getValidationErrors = (step) => {
    const errors = [];
    switch (step) {
      case 0:
        // Prima verificare: CAEN neeligibil (blocant)
        if (caenValid === false) {
          errors.push('❌ COD CAEN NEELIGIBIL - Nu puteți continua. Contactați administratorul la admin@finbro.ro');
          return errors; // Returnează imediat, nu permite alte validări
        }
        if (!formData.companyName) errors.push('Denumirea firmei');
        if (!formData.cui) errors.push('CUI');
        if (!cuiVerified) errors.push('Verificare CUI la ANAF (obligatorie)');
        if (!formData.jNumber) errors.push('Numărul de înregistrare (J)');
        if (!formData.address || formData.address.length < 10) errors.push('Adresa completă (minim 10 caractere)');
        break;
      case 1:
        if (!formData.representativeName) errors.push('Numele reprezentantului');
        if (!formData.representativeCnp || formData.representativeCnp.length !== 13) errors.push('CNP reprezentant (13 cifre)');
        if (!formData.representativeIdSeries) errors.push('Seria actului de identitate');
        if (!formData.representativeIdNumber || formData.representativeIdNumber.length < 6) errors.push('Numărul actului (minim 6 cifre)');
        if (!formData.representativeIdIssuer) errors.push('Emitentul actului');
        if (!formData.representativeAddress || formData.representativeAddress.length < 10) errors.push('Adresa reprezentantului (minim 10 caractere)');
        break;
      case 2:
        if (!formData.email || !formData.email.includes('@')) errors.push('Email valid');
        if (!formData.phone || formData.phone.length < 10) errors.push('Telefon (minim 10 cifre)');
        // Validare parolă
        if (!formData.password || formData.password.length < 8) {
          errors.push('Parolă (minim 8 caractere)');
        } else {
          // Verificare complexitate parolă
          const hasUpperCase = /[A-Z]/.test(formData.password);
          const hasLowerCase = /[a-z]/.test(formData.password);
          const hasNumbers = /\d/.test(formData.password);
          const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(formData.password);
          
          if (!hasUpperCase) errors.push('Parola trebuie să conțină cel puțin o literă mare');
          if (!hasLowerCase) errors.push('Parola trebuie să conțină cel puțin o literă mică');
          if (!hasNumbers) errors.push('Parola trebuie să conțină cel puțin o cifră');
          if (!hasSpecial) errors.push('Parola trebuie să conțină cel puțin un caracter special (!@#$%^&*)');
        }
        if (!formData.confirmPassword) {
          errors.push('Confirmare parolă');
        } else if (formData.password !== formData.confirmPassword) {
          errors.push('Parolele nu coincid');
        }
        break;
      default:
        break;
    }
    return errors;
  };

  const validateStep = (step) => {
    return getValidationErrors(step).length === 0;
  };

  const handleNext = () => {
    const errors = getValidationErrors(currentStep);
    if (errors.length === 0) {
      setCurrentStep(currentStep + 1);
      setError('');
    } else {
      setError(`Câmpuri incomplete sau invalide: ${errors.join(', ')}`);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = getValidationErrors(2);
    if (errors.length > 0) {
      setError(`Câmpuri incomplete sau invalide: ${errors.join(', ')}`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await register(formData);
      
      if (result.success) {
        setSuccess(true);
        toast({
          title: "✅ Înregistrare realizată!",
          description: result.message
        });
      } else {
        // Handle error message properly - it might be an object or string
        let errorMessage = 'Eroare la înregistrare';
        if (typeof result.message === 'string') {
          errorMessage = result.message;
        } else if (Array.isArray(result.message)) {
          errorMessage = result.message.map(e => e.msg || e).join(', ');
        } else if (typeof result.message === 'object' && result.message !== null) {
          errorMessage = JSON.stringify(result.message);
        }
        setError(errorMessage);
      }
    } catch (error) {
      setError('A apărut o eroare. Vă rugăm încercați din nou.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center shadow-lg">
          <CardContent className="p-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Înregistrare Completă!</h2>
            <p className="text-gray-600 mb-6">
              Cererea dumneavoastră a fost trimisă cu succes. Veți fi contactat după validarea datelor.
            </p>
            <Button onClick={() => navigate('/login')} className="w-full">
              Mergeți la Autentificare
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const renderStep0 = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="companyName">Nume Companie *</Label>
          <Input
            id="companyName"
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            placeholder="Ex: Credit SRL"
            required
            className={cuiVerified ? "border-green-500 bg-green-50" : ""}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="cui">CUI * {cuiVerified && <span className="text-green-600 text-sm">(verificat ANAF)</span>}</Label>
          <div className="flex gap-2">
            <Input
              id="cui"
              name="cui"
              value={formData.cui}
              onChange={(e) => {
                handleChange(e);
                setCuiVerified(false);
              }}
              placeholder="Ex: 12345678"
              required
              className={cuiVerified ? "border-green-500 bg-green-50" : ""}
            />
            <Button 
              type="button" 
              onClick={verifyCui} 
              disabled={loadingCui || !formData.cui}
              variant="outline"
              className="shrink-0"
            >
              {loadingCui ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Search className="h-4 w-4 mr-1" />
                  Verifică ANAF
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-gray-500">Introduceți CUI-ul și apăsați "Verifică ANAF" pentru auto-completare</p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="jNumber">Numărul de Înregistrare (J) *</Label>
        <Input
          id="jNumber"
          name="jNumber"
          value={formData.jNumber}
          onChange={handleChange}
          placeholder="Ex: J40/12345/2023"
          required
          className={cuiVerified ? "border-green-500 bg-green-50" : ""}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="codCAEN">
          Cod CAEN Principal * 
          {cuiVerified && caenValid === true && (
            <span className="text-green-600 text-sm ml-2">✓ Cod valid pentru intermediari</span>
          )}
          {cuiVerified && caenValid === false && (
            <span className="text-red-600 text-sm ml-2">✗ Cod CAEN neeligibil</span>
          )}
        </Label>
        <Input
          id="codCAEN"
          name="codCAEN"
          value={formData.codCAEN}
          onChange={handleChange}
          placeholder="Ex: 6619"
          required
          readOnly={cuiVerified}
          className={
            cuiVerified 
              ? caenValid === true 
                ? "border-green-500 bg-green-50 cursor-not-allowed" 
                : "border-red-500 bg-red-50 cursor-not-allowed"
              : ""
          }
        />
        {cuiVerified && caenValid === false && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-700 font-medium">
              ❌ Codul CAEN "{formData.codCAEN}" nu este acceptat pe platforma FINBRO.
            </p>
            <p className="text-sm text-red-600 mt-1">
              Vă rugăm să contactați administratorul la adresa <a href="mailto:admin@finbro.ro" className="underline font-semibold">admin@finbro.ro</a> pentru asistență.
            </p>
          </div>
        )}
        {!cuiVerified && (
          <p className="text-xs text-amber-600 font-medium">
            ⚠️ Coduri CAEN acceptate: 6619 (Intermedieri financiare), 6492, 6499. 
            Dacă firma nu deține unul dintre aceste coduri CAEN, înregistrarea nu va fi acceptată.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="address">Adresa Completă *</Label>
        <Textarea
          id="address"
          name="address"
          value={formData.address}
          onChange={handleChange}
          placeholder="Adresa completă a sediului social"
          rows={3}
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="bankAccount">Cont Bancar - IBAN (opțional)</Label>
          <Input
            id="bankAccount"
            name="bankAccount"
            value={formData.bankAccount}
            onChange={handleChange}
            placeholder="RO49AAAA1B31007593840000"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="bankName">Nume Bancă (opțional)</Label>
          <Input
            id="bankName"
            name="bankName"
            value={formData.bankName}
            onChange={handleChange}
            placeholder="Ex: BRD - Groupe Société Générale"
          />
        </div>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="representativeName">Nume și Prenume Reprezentant *</Label>
        <Input
          id="representativeName"
          name="representativeName"
          value={formData.representativeName}
          onChange={handleChange}
          placeholder="Ex: Ion Popescu"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="representativeCnp">CNP Reprezentant *</Label>
        <Input
          id="representativeCnp"
          name="representativeCnp"
          value={formData.representativeCnp}
          onChange={handleChange}
          placeholder="1234567890123"
          maxLength={13}
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="representativeIdSeries">Serie Act Identitate *</Label>
          <Input
            id="representativeIdSeries"
            name="representativeIdSeries"
            value={formData.representativeIdSeries}
            onChange={handleChange}
            placeholder="Ex: AB"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="representativeIdNumber">Număr Act *</Label>
          <Input
            id="representativeIdNumber"
            name="representativeIdNumber"
            value={formData.representativeIdNumber}
            onChange={handleChange}
            placeholder="Ex: 123456"
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="representativeIdIssuer">Emitent Act Identitate *</Label>
        <Input
          id="representativeIdIssuer"
          name="representativeIdIssuer"
          value={formData.representativeIdIssuer}
          onChange={handleChange}
          placeholder="Ex: SPCLEP București"
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="representativeIdIssueDate">Data Eliberare</Label>
          <Input
            id="representativeIdIssueDate"
            name="representativeIdIssueDate"
            type="date"
            value={formData.representativeIdIssueDate}
            onChange={handleChange}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="representativeIdExpiryDate">Data Expirare</Label>
          <Input
            id="representativeIdExpiryDate"
            name="representativeIdExpiryDate"
            type="date"
            value={formData.representativeIdExpiryDate}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="representativeAddress">Adresa din Act Identitate *</Label>
        <Textarea
          id="representativeAddress"
          name="representativeAddress"
          value={formData.representativeAddress}
          onChange={handleChange}
          placeholder="Adresa din actul de identitate"
          rows={3}
          required
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="contact@companie.ro"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Telefon *</Label>
          <Input
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="0721234567"
            required
          />
        </div>
      </div>

      {/* Câmpuri Parolă */}
      <div className="border-t pt-4 mt-4">
        <h4 className="font-medium text-gray-900 mb-3">Setează Parola Contului *</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="password">Parolă *</Label>
            <Input
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Minim 8 caractere"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirmă Parola *</Label>
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Reintrodu parola"
              required
            />
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Parola trebuie să conțină minim 8 caractere, o literă mare, o literă mică, o cifră și un caracter special (!@#$%^&*)
        </p>
        {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
          <p className="text-xs text-red-500 mt-1">Parolele nu coincid!</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="website">Website (opțional)</Label>
        <Input
          id="website"
          name="website"
          value={formData.website}
          onChange={handleChange}
          placeholder="www.companie.ro"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="headerColor">Culoare Antet PDF</Label>
        <div className="flex gap-2 items-center">
          <Input
            id="headerColor"
            name="headerColor"
            type="color"
            value={formData.headerColor}
            onChange={handleChange}
            className="w-20 h-10"
          />
          <span className="text-sm text-gray-600">Pentru personalizarea PDF-urilor</span>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="desiredPlan">Plan Abonament Dorit</Label>
        <Select value={formData.desiredPlan} onValueChange={(value) => handleSelectChange('desiredPlan', value)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="basic">BASIC - Gratuit</SelectItem>
            <SelectItem value="professional">PROFESIONAL - 99 RON/lună</SelectItem>
            <SelectItem value="premium">PREMIUM - 199 RON/lună</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="isAnpcIntermediary"
          checked={formData.isAnpcIntermediary}
          onCheckedChange={(checked) => handleCheckboxChange('isAnpcIntermediary', checked)}
        />
        <Label htmlFor="isAnpcIntermediary">Sunt intermediar avizat ANPC</Label>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-green-600 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Building2 className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Înregistrare Intermediar de Credite</h1>
          <p className="text-gray-600 mt-2">Completați datele pentru a crea contul de intermediar</p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center space-x-4">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;
              
              return (
                <div key={index} className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                    isActive ? 'border-blue-600 bg-blue-600 text-white' :
                    isCompleted ? 'border-green-600 bg-green-600 text-white' :
                    'border-gray-300 bg-white text-gray-400'
                  }`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`w-16 h-0.5 mx-2 ${
                      isCompleted ? 'bg-green-600' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <Card className="shadow-lg border-0">
          <CardHeader>
            <CardTitle className="text-center">{steps[currentStep].title}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit}>
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {currentStep === 0 && renderStep0()}
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}

              <div className="flex justify-between items-center mt-8">
                <div>
                  {currentStep > 0 ? (
                    <Button type="button" variant="outline" onClick={handleBack}>
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Înapoi
                    </Button>
                  ) : (
                    <Link to="/login">
                      <Button type="button" variant="outline">
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        La Autentificare
                      </Button>
                    </Link>
                  )}
                </div>

                <span className="text-sm text-gray-600">
                  Pasul {currentStep + 1} din {steps.length}
                </span>

                <div>
                  {currentStep < steps.length - 1 ? (
                    <Button type="button" onClick={handleNext}>
                      Continuă →
                    </Button>
                  ) : (
                    <Button type="submit" disabled={loading}>
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Se înregistrează...
                        </>
                      ) : (
                        <>
                          <UserPlus className="mr-2 h-4 w-4" />
                          Trimite Cererea
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Register;